import React, { useState } from 'react';

export const GroupsBreadcrumbs = ({ items, compact = true }) => {
  if (!items?.length) return null;
  return (
    <nav
      className={`groups-crumbs${compact ? ' groups-crumbs--compact' : ''}`}
      aria-label="Breadcrumb"
    >
      {items.map((item, i) => (
        <React.Fragment key={item.key || i}>
          {i > 0 && <span className="groups-crumb-sep">/</span>}
          {item.onClick ? (
            <button type="button" className="groups-crumb-btn" onClick={item.onClick}>
              {item.label}
            </button>
          ) : (
            <span className="groups-crumb-current">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

const GroupsListTable = ({
  eyebrow,
  hint,
  count,
  search,
  onSearch,
  searchPlaceholder = 'Search…',
  toolbar,
  columns = [],
  rows = [],
  emptyMessage = 'No results.',
  loading = false,
  gridTemplate,
  tableClassName = '',
  searchListSize,
  headerExtra,
  barLayout = 'default'
}) => {
  const [hoverId, setHoverId] = useState(null);
  const gridStyle = gridTemplate ? { gridTemplateColumns: gridTemplate } : undefined;
  const listSize = searchListSize ?? rows.length;
  const showSearch = Boolean(onSearch) && listSize > 10;

  return (
    <section className="groups-panel">
      {(eyebrow || toolbar || showSearch || headerExtra) && (
        <div className={`groups-panel-bar${barLayout === 'inline' ? ' groups-panel-bar--inline' : ''}`}>
          <div className="groups-panel-bar-main">
            {eyebrow && (
              <div className="groups-panel-eyebrow">
                {eyebrow}
                {typeof count === 'number' && <span className="groups-count">{count}</span>}
              </div>
            )}
            {barLayout !== 'inline' && hint && <p className="groups-panel-hint">{hint}</p>}
            {barLayout !== 'inline' && headerExtra}
          </div>
          <div className="groups-panel-tools">
            {barLayout !== 'inline' && showSearch && (
              <input
                type="search"
                className="groups-search"
                value={search ?? ''}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
            )}
            {barLayout === 'inline' ? headerExtra || toolbar : toolbar}
          </div>
        </div>
      )}

      {loading ? (
        <div className="groups-state">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="groups-empty">{emptyMessage}</div>
      ) : (
        <div className={`groups-table ${tableClassName}`.trim()}>
          <div className="groups-table-head" style={gridStyle}>
            {columns.map((col) => (
              <div
                key={col.key || col.label}
                className={col.align === 'right' ? 'groups-align-right' : undefined}
              >
                {col.label}
              </div>
            ))}
          </div>
          {rows.map((row) => {
            const isHover = hoverId === row.id;
            const rowClass = `groups-table-row${isHover ? ' groups-table-row--hover' : ''}${
              row.href ? ' groups-table-row--link' : ''
            }`;
            const rowProps = {
              className: rowClass,
              style: gridStyle,
              onMouseEnter: () => setHoverId(row.id),
              onMouseLeave: () => setHoverId((h) => (h === row.id ? null : h)),
              onClick: row.onClick,
              onKeyDown: row.onClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      row.onClick(e);
                    }
                  }
                : undefined
            };

            const content = row.cells.map((cell, ci) => (
              <div
                key={ci}
                className={`groups-table-cell${columns[ci]?.align === 'right' ? ' groups-align-right' : ''}${
                  columns[ci]?.muted ? ' groups-muted' : ''
                }`}
              >
                {cell}
              </div>
            ));

            if (row.href) {
              return (
                <a key={row.id} href={row.href} {...rowProps}>
                  {content}
                </a>
              );
            }

            return (
              <div
                key={row.id}
                role={row.onClick ? 'button' : undefined}
                tabIndex={row.onClick ? 0 : undefined}
                {...rowProps}
              >
                {content}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default GroupsListTable;
